<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <div class="sl-main">
        <div v-if="!initializing" class="sl-page sl-page--full sl-page--paddless">
            <!-- Eyes Header (Right and Left) -->
            <eyes-header :class="showPrototypesSelection ? 'mb-4' : ''" />

            <template v-if="showPrototypesSelection">
                <!-- Renewal Form (Right and Left) -->
                <div v-if="!isOrderEdition" class="flex px-4">
                    <renewal-form
                        v-model:order-identifier="sides.right.orderIdentifier"
                        :other-side-order-identifier="sides.left.orderIdentifier"
                        :error="sides.right.error"
                        side="right"
                        class="w-1/2 mr-2"
                        @next="next"
                    />

                    <renewal-form
                        v-model:order-identifier="sides.left.orderIdentifier"
                        :other-side-order-identifier="sides.right.orderIdentifier"
                        :error="sides.left.error"
                        side="left"
                        class="w-1/2 ml-2"
                        @next="next"
                    />
                </div>

                <!-- "or" Text Line -->
                <div v-if="!isOrderEdition" class="flex items-center my-3">
                    <div class="w-1/2 text-primary-500 text-center">
                        {{ $t('common.or') }}
                    </div>

                    <!-- "Next" Button -->
                    <div v-if="!editPrototypesSelection" class="flex justify-center">
                        <button class="lsn-btn" :class="nextButtonClass" @click="next">
                            {{ $t('common.next') }}
                        </button>
                    </div>

                    <div class="w-1/2 text-primary-500 text-center">
                        {{ $t('common.or') }}
                    </div>
                </div>

                <!-- Prototypes Lists (Right and Left) -->
                <div class="flex px-4">
                    <prototypes-list
                        :label="isOrderEdition
                            ? $t('order.edit_order')
                            : $t('order.new_order')"
                        :prototype-code="sides.right.prototypeCode"
                        :disabled="isOrderEdition && !sides.right.prototypeCode"
                        :disabled-text="$t('order.cannot_add_prototype_when_editing')"
                        side="right"
                        class="w-1/2 mr-2"
                        @prototype-code:set="onPrototypeSet"
                    />

                    <prototypes-list
                        :label="isOrderEdition
                            ? $t('order.edit_order')
                            : $t('order.new_order')"
                        :prototype-code="sides.left.prototypeCode"
                        :disabled="isOrderEdition && !sides.left.prototypeCode"
                        :disabled-text="$t('order.cannot_add_prototype_when_editing')"
                        side="left"
                        class="w-1/2 ml-2"
                        @prototype-code:set="onPrototypeSet"
                    />
                </div>

                <!-- "Next" Button -->
                <div v-if="!editPrototypesSelection" class="flex justify-center my-6">
                    <button class="lsn-btn" :class="nextButtonClass" @click="next">
                        {{ $t('common.next') }}
                    </button>
                </div>

                <!-- Bottom Buttons -->
                <div v-if="editPrototypesSelection" class="flex justify-center my-6">
                    <!-- Cancel -->
                    <button
                        class="lsn-btn lsn-btn--gray-outline mr-4"
                        @click="editPrototypesSelection = false"
                    >
                        {{ $t('common.cancel') }}
                    </button>

                    <!-- Edit -->
                    <button
                        class="lsn-btn lsn-btn--primary sl-order__submit"
                        :class="isEditButtonActive
                            ? 'lsn-btn--primary sl-order__submit'
                            : 'lsn-btn--gray-outline lsn-btn--disabled'"
                        @click="next"
                    >
                        {{ $t('common.edit') }}
                    </button>
                </div>
            </template>

            <template v-if="showLensForms">
                <!-- Prototypes Display (Right and Left) -->
                <div class="flex items-center justify-center">
                    <selected-prototypes
                        @edit-prototype-selection="editPrototypesSelection = true"
                    />
                </div>

                <!-- Order Form -->
                <lens-order-form />

                <!-- Bottom Buttons (after the form) -->
                <div class="flex justify-center items-center gap-4 my-6">
                    <!-- "Add to Basket" Button -->
                    <button
                        v-if="!isOrderEdition"
                        class="lsn-btn lsn-btn--primary sl-order__submit"
                        :tabindex="3"
                        @click="addToBasket()"
                    >
                        {{ addToBasketButtonLabel }}
                    </button>

                    <template v-if="isOrderEdition">
                        <!-- "Save" Button -->
                        <button
                            class="lsn-btn lsn-btn--primary-outline sl-order__submit"
                            :tabindex="3"
                            @click="updateOrders()"
                        >
                            {{ $t('common.save') }}
                        </button>

                        <!-- "Production" Button -->
                        <button
                            class="lsn-btn lsn-btn--primary sl-order__submit"
                            :tabindex="3"
                            @click="productionBtn_onClick"
                        >
                            {{ $t('order.edit.send_to_production.label') }}
                        </button>
                    </template>
                </div>
            </template>
        </div>

        <!-- Loader (while initializing or loading) -->
        <div v-if="initializing || loading" class="mt-8 mx-auto text-center">
            <img class="inline" src="/img/loader-5.gif">
        </div>

        <!-- Re-order: not same entity modal warning -->
        <choose-entity-modal
            :is-open="showChooseEntityModal"
            :sides="sides"
            @close="showChooseEntityModal = false"
            @next="dispatch"
        />

        <!-- Re-order: not same patient modal warning -->
        <choose-patient-modal
            :is-open="showChoosePatientModal"
            :sides="sides"
            @close="showChoosePatientModal = false"
            @next="dispatch"
        />

        <!-- Re-order: side inconsistency warning -->
        <choose-side-modal
            :is-open="showChooseSideModal"
            :sides="sides"
            @close="showChooseSideModal = false"
            @next="dispatch"
            @invert="invertAndDispatch"
        />

        <!-- Dispatch Error -->
        <teleport to="body">
            <lsn-modal :is-open="dispatchError !== null">
                <div class="sl-prompt sl-prompt--danger text-center">
                    <div class="sl-prompt__contents">
                        <div class="sl-prompt__title">
                            {{ dispatchErrorTitle }}
                        </div>

                        <div class="sl-prompt__text">
                            {{ dispatchErrorText }}
                        </div>
                    </div>

                    <div class="sl-prompt__actions">
                        <button
                            class="sl-prompt__button sl-prompt__button--white"
                            @click="dispatchError = null"
                        >
                            {{ $t('common.close') }}
                        </button>
                    </div>
                </div>
            </lsn-modal>
        </teleport>

        <!-- Pricing Error -->
        <teleport to="body">
            <lsn-modal :is-open="showPricingError">
                <div class="sl-prompt sl-prompt--info text-center">
                    <div class="sl-prompt__contents">
                        <div class="sl-prompt__title">
                            {{ $t('order.errors.pricing.article_unavailable.title') }}
                        </div>

                        <div class="sl-prompt__text">
                            {{ $t('order.errors.pricing.article_unavailable.description') }}
                        </div>
                    </div>

                    <div class="sl-prompt__actions">
                        <button
                            class="sl-prompt__button sl-prompt__button--white"
                            @click="showPricingError = false"
                        >
                            {{ $t('common.close') }}
                        </button>
                    </div>
                </div>
            </lsn-modal>
        </teleport>

        <!-- Order Tools -->
        <teleport to="body">
            <div class="sl-order-tools">
                <!-- Fitting Selector Button -->
                <div
                    v-if="showLensForms"
                    v-tippy="{ placement: 'right' }"
                    :content="$t(fittingsPanelOpen
                        ? 'common.close'
                        : 'fitting_selector.title')"
                    class="sl-order-tools__button"
                    @click="toggleFittingsPanel"
                >
                    <mdi-icon
                        icon="mdiSwapHorizontalCircleOutline"
                        class="transform transition-transform duration-300"
                        :class="fittingsPanelOpen ? 'rotate-180' : 'rotate-0'"
                    />
                </div>

                <!-- Calculators Button -->
                <div
                    v-if="showLensForms"
                    v-tippy="{ placement: 'right' }"
                    :content="$t('calculators.calculators')"
                    class="sl-order-tools__button sl-calculators-button"
                    @click="toggleCalculatorPanels"
                >
                    <mdi-icon icon="mdiCalculator" />
                </div>

                <!-- Plot Graph Button -->
                <div
                    id="sl-order-tools__plot-graph-button"
                    class="flex gap-2"
                >
                </div>

                <!-- Convert Prototype Button -->
                <div
                    id="sl-order-tools__convert-prototype-button"
                    class="flex gap-2"
                >
                </div>

                <!-- Review Comments Buttons -->
                <div class="flex gap-2">
                    <template v-for="side in ['right', 'left']" :key="side">
                        <div
                            v-if="showLensForms && reviewRequest[side]"
                            v-tippy="{ placement: 'right' }"
                            :content="$t('review_requests.view_comments')"
                            class="sl-order-tools__button"
                            @click="reviewCommentsOpen[side] = !reviewCommentsOpen[side]"
                        >
                            <mdi-icon icon="mdiMessageTextOutline" />
                        </div>
                    </template>
                </div>
            </div>
        </teleport>

        <!-- Order Tools (Staff) -->
        <teleport to="body">
            <div
                v-if="$store.getters['account/isStaff']"
                class="sl-order-tools sl-order-tools--staff"
            >
                <!-- View as Customer Button (aka "Ninja Mode" or "Cheat Code") -->
                <div
                    v-if="showLensForms"
                    v-tippy="{ placement: 'left' }"
                    :content="$t(viewAsCustomer ? 'No Cheating' : 'Cheat Code')"
                    class="sl-order-tools__button"
                    :class="{ 'sl-view-as-customer-button--active': viewAsCustomer }"
                    @click="toggleViewAsCustomer"
                >
                    <mdi-icon icon="mdiNinja" />
                </div>

                <!-- Reset Back Surface Button -->
                <div
                    id="sl-order-tools__reset-back-surface-button"
                    class="flex gap-2"
                >
                </div>
            </div>
        </teleport>

        <!-- Over-Refraction Panel -->
        <lsn-sider
            v-if="showLensForms && hasOverRefCalculator"
            :is-open="calculatorPanelsOpen"
            class="sl-widgets w-[22rem]"
        >
            <sl-over-refraction-calculator @close="closeCalculatorPanels" />
        </lsn-sider>

        <!-- Fitting Selector Panel -->
        <lsn-sider
            v-if="showLensForms"
            :is-open="fittingsPanelOpen"
            side="left"
            class="sl-widgets w-80"
        >
            <sl-fitting-selector class="sl-widget" @close="closeFittingsPanel" />
        </lsn-sider>

        <!-- Calculators Panel -->
        <lsn-sider
            v-if="showLensForms && hasCalculators"
            :is-open="calculatorPanelsOpen"
            side="left"
            class="sl-widgets w-80"
        >
            <sl-calculators @close="closeCalculatorPanels" />
        </lsn-sider>

        <!-- Review Comments Panels -->
        <template v-for="side in ['right', 'left']" :key="side">
            <review-request-comments
                v-if="showLensForms && reviewRequest[side]"
                :is-open="reviewCommentsOpen[side]"
                :request="reviewRequest[side]"
                @close="reviewCommentsOpen[side] = false"
            />
        </template>

        <!-- "Confirm Sending to Production" Dialog -->
        <teleport to="body">
            <lsn-modal :is-open="confirmProductionDialogOpen">
                <div class="sl-prompt sl-prompt--danger text-center">
                    <div class="sl-prompt__contents">
                        <div class="sl-prompt__title">
                            {{ $t('order.edit.send_to_production.confirm.title', nbFittings) }}
                        </div>

                        <div class="sl-prompt__text">
                            {{ $t('order.edit.send_to_production.confirm.text', nbFittings) }}
                        </div>
                    </div>

                    <div class="sl-prompt__actions">
                        <button
                            class="sl-prompt__button sl-prompt__button--white"
                            @click="confirmProductionDialogOpen = false"
                        >
                            {{ $t('common.close') }}
                        </button>

                        <button
                            class="sl-prompt__button sl-prompt__button--red"
                            @click="releaseOrdersFromReview"
                        >
                            {{ $t('order.edit.send_to_production.label') }}
                        </button>
                    </div>
                </div>
            </lsn-modal>
        </teleport>
    </div>
</template>


<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>
import EyesHeader                                from './EyesHeader.vue';
import RenewalForm                               from './RenewalForm.vue';
import SelectedPrototypes                        from './SelectedPrototypes.vue';
import PrototypesList                            from './PrototypesList.vue';
import ChooseEntityModal                         from './ChooseEntityModal.vue';
import ChoosePatientModal                        from './ChoosePatientModal.vue';
import ChooseSideModal                           from './ChooseSideModal.vue';
import LensOrderForm                             from './form-lines/FormLines.vue';
import SlOverRefractionCalculator                from './OverRefractionCalculator.vue';
import SlFittingSelector                         from './FittingSelector.vue';
import SlCalculators                             from './Calculators.vue';
import ReviewRequestComments                     from '@/components/review-requests/ReviewRequestComments.vue';
import {
    default as LensOrderUrlManager,
    ID_TYPES,
}                                                from './LensOrderUrlManager';
import { ONLY_NUMBERS }                          from '@/utils/Regexes';
import ReviewRequest                             from '@/models/ReviewRequest';
import UnknownPrototypeError                     from '@/errors/UnknownPrototypeError';
import useFittingComposable                      from '@/composables/FittingComposable';
import useOfgComposable                          from '@/composables/OfgComposable';

// Chain of responsibility handlers: "dispatch"
import ResetErrors                               from './handlers/ResetErrors';
import ResetSideFittingOnConditions              from './handlers/ResetSideFittingOnConditions';
import FetchPreviousOrderByReference             from './handlers/FetchPreviousOrderByReference';
import FetchPreviousOrderByLensSn                from './handlers/FetchPreviousOrderByLensSn';
import FetchPreviousOrderByLensOemNumber         from './handlers/FetchPreviousOrderByLensOemNumber';
import CheckForSomethingOrStop                   from './handlers/CheckForSomethingOrStop';
import ValidatePreviousOrdersEntitiesConsistency from './handlers/ValidatePreviousOrdersEntitiesConsistency';
import ValidatePreviousOrdersPatientsConsistency from './handlers/ValidatePreviousOrdersPatientsConsistency';
import ValidateLensSidesConsistency              from './handlers/ValidateLensSidesConsistency';
import SilentlySetPrototypeCodesInUrl            from './handlers/SilentlySetPrototypeCodesInUrl';
import CreateFittingIfNotExists                  from './handlers/CreateFittingIfNotExists';
import SetFittingPrototype                       from './handlers/SetFittingPrototype';
import SetFittingValuesByPreviousOrder           from './handlers/SetFittingValuesByPreviousOrder';
import SetFittingValuesByBasketLine              from './handlers/SetFittingValuesByBasketLine';
import SetFittingSessionId                       from './handlers/SetFittingSessionId';
import SetPrototypePreviousValuesByPreviousOrder from './handlers/SetPrototypePreviousValuesByPreviousOrder';
import useSetInitialLensSide                     from './handlers/SetInitialLensSide';
import FetchReviewRequest                        from './handlers/FetchReviewRequest';
import TemplatePrototypeForms                    from './handlers/TemplatePrototypeForms';
import SetPrototypesSide                         from './handlers/SetPrototypesSide';
import CompleteGroupedParameterValues            from './handlers/CompleteGroupedParameterValues';

// Chain of responsibility handlers: "add to basket"
import ValidateLenses                            from './handlers/ValidateLenses';
import PriceLenses                               from './handlers/PriceLenses';
import useSetLensPairId                          from './handlers/SetLensPairId';
import useInitializeAddToBasketContext           from './handlers/InitializeAddToBasketContext';
import useConvertFittingsToBasketLines           from './handlers/ConvertFittingsToBasketLines';
import useLinkLinesToBeSaved                     from './handlers/LinkLinesToBeSaved';
import useAddToBasket                            from './handlers/AddToBasket';
import NavigateToBasket                          from './handlers/NavigateToBasket';

// Chain of responsibility handlers: "update order"
import UpdateOrders                              from './handlers/UpdateOrders';
import RefreshWaitingForReviewOrdersList         from './handlers/RefreshWaitingForReviewOrdersList';
import useNavigate                               from './handlers/Navigate';

// Chain of responsibility maps (used as queues)
const dispatchQueue    = new Map();
const addToBasketQueue = new Map();

// Constants
const SIDE_RIGHT = 'right';
const SIDE_LEFT  = 'left';

/** @constant {Array} SUPPORTED_ID_TYPES List of order identifier types supported by `RenewalForm`. */
const SUPPORTED_ID_TYPES = [
    ID_TYPES.LENS_SN,
    ID_TYPES.LENS_OEM_NUMBER,
];

export const PROTOTYPES_WITHOUT_CALCULATORS = [
    // NightFlex
    'NIGHTFLEX_SYMMETRICAL_MYOPIA',
    'NIGHTFLEX_SYMMETRICAL_HIGH_MYOPIA',
    'NIGHTFLEX_SYMMETRICAL_HYPEROPIA',
    'NIGHTFLEX_SYMMETRICAL_PRESBYOPIA',
    'NIGHTFLEX_SYMMETRICAL_RELAX',
    'NIGHTFLEX_ASYMMETRICAL_MYOPIA',
    'NIGHTFLEX_ASYMMETRICAL_HIGH_MYOPIA',
    'NIGHTFLEX_ASYMMETRICAL_HYPEROPIA',
    'NIGHTFLEX_ASYMMETRICAL_PRESBYOPIA',
    'NIGHTFLEX_ASYMMETRICAL_RELAX',
];

// Hide the over-refraction calculator in prototypes where it doesn't apply.
export const PROTOTYPES_WITHOUT_OVER_REFRACTION_CALCULATOR = [
    // ToriFlex
    'TORIFLEX_SA2_BITORIC',
    'TORIFLEX_SA2_FRONTTORIC',

    // ToreliFlex
    'TORELIFLEX_SA2_BITORIC',
    'TORELIFLEX_SA2_FRONTTORIC',

    // ToriFlex Relax
    'TORIFLEXRELAX_SA2_BITORIC',
    'TORIFLEXRELAX_SA2_FRONTTORIC',

    // NightFlex
    'NIGHTFLEX_SYMMETRICAL_MYOPIA',
    'NIGHTFLEX_SYMMETRICAL_HIGH_MYOPIA',
    'NIGHTFLEX_SYMMETRICAL_HYPEROPIA',
    'NIGHTFLEX_SYMMETRICAL_PRESBYOPIA',
    'NIGHTFLEX_SYMMETRICAL_RELAX',
    'NIGHTFLEX_ASYMMETRICAL_MYOPIA',
    'NIGHTFLEX_ASYMMETRICAL_HIGH_MYOPIA',
    'NIGHTFLEX_ASYMMETRICAL_HYPEROPIA',
    'NIGHTFLEX_ASYMMETRICAL_PRESBYOPIA',
    'NIGHTFLEX_ASYMMETRICAL_RELAX',

    // NightFlex 2025
    'NIGHTFLEX2025_MYOPIA',
    'NIGHTFLEX2025_HIGH_MYOPIA',
    'NIGHTFLEX2025_HYPEROPIA',
    'NIGHTFLEX2025_PRESBYOPIA',
    'NIGHTFLEX2025_RELAX',

    'NIGHTFLEX2025TEST_MYOPIA',
    'NIGHTFLEX2025TEST_HIGH_MYOPIA',
    'NIGHTFLEX2025TEST_HYPEROPIA',
    'NIGHTFLEX2025TEST_PRESBYOPIA',
    'NIGHTFLEX2025TEST_RELAX',

    // NightFlex Liberty
    'NIGHTFLEXLIBERTY_MYOPIA',
    'NIGHTFLEXLIBERTY_HIGH_MYOPIA',
    'NIGHTFLEXLIBERTY_HYPEROPIA',
    'NIGHTFLEXLIBERTY_PRESBYOPIA',
    'NIGHTFLEXLIBERTY_RELAX',

    // ToriFlex Relax
    'SOURISFLEX_SMS',
];

export default
{
    name: 'OrderLenses',

    components:
    {
        EyesHeader,
        RenewalForm,
        SelectedPrototypes,
        PrototypesList,
        ChooseEntityModal,
        ChoosePatientModal,
        ChooseSideModal,
        LensOrderForm,
        SlOverRefractionCalculator,
        SlFittingSelector,
        SlCalculators,
        ReviewRequestComments,
    },

    setup()
    {
        const { getLensLetter } = useFittingComposable();
        const { isOfgAccessible } = useOfgComposable();
        const SetInitialLensSide = useSetInitialLensSide();
        const SetLensPairId = useSetLensPairId();
        const InitializeAddToBasketContext = useInitializeAddToBasketContext();
        const ConvertFittingsToBasketLines = useConvertFittingsToBasketLines();
        const LinkLinesToBeSaved = useLinkLinesToBeSaved();
        const AddToBasket = useAddToBasket();
        const Navigate = useNavigate();

        return {
            getLensLetter,
            isOfgAccessible,
            SetInitialLensSide,
            SetLensPairId,
            InitializeAddToBasketContext,
            ConvertFittingsToBasketLines,
            LinkLinesToBeSaved,
            AddToBasket,
            Navigate,
        };
    },

    data()
    {
        return {
            initializing:            false,
            loading:                 false,
            editPrototypesSelection: false,
            showChooseEntityModal:   false,
            showChoosePatientModal:  false,
            showChooseSideModal:     false,
            showPricingError:        false,
            dispatchOnRouteChange:   true,
            isEditButtonActive:      false,
            fittingsPanelOpen:       false,
            calculatorPanelsOpen:    false,

            confirmProductionDialogOpen: false,

            reviewCommentsOpen: {
                right: false,
                left:  false,
            },

            reviewRequest: {
                /** @type {?ReviewRequest} */
                right: null,

                /** @type {?ReviewRequest} */
                left: null,
            },

            /** @type {LensOrderUrlManager} */
            lensOrderUrlManager: null,

            sides:
            {
                right:
                {
                    orderIdentifier: null,
                    prototypeCode:   null,
                    previousOrder:   null,
                    error:           null,
                },
                left:
                {
                    orderIdentifier: null,
                    prototypeCode:   null,
                    previousOrder:   null,
                    error:           null,
                },
            },
            dispatchError: null,
        };
    },

    computed:
    {
        isNextButtonActive()
        {
            return !this.sides.right.error &&
                !this.sides.left.error     &&
                (
                    this.sides.left.prototypeCode   ||
                    this.sides.right.prototypeCode  ||
                    this.sides.left.orderIdentifier ||
                    this.sides.right.orderIdentifier
                );
        },

        nextButtonClass()
        {
            return this.isNextButtonActive
                ? 'lsn-btn--primary sl-order__submit'
                : 'lsn-btn--gray-outline lsn-btn--disabled';
        },

        showPrototypesSelection()
        {
            return !this.loading && (!this.showLensForms || this.editPrototypesSelection);
        },

        showLensForms()
        {
            const rightPrototype = this.$store.getters['lensOrder/getPrototype'](SIDE_RIGHT);
            const leftPrototype  = this.$store.getters['lensOrder/getPrototype'](SIDE_LEFT);

            return !this.loading && (rightPrototype || leftPrototype);
        },

        hasOverRefCalculator()
        {
            const rightPrototypeCode = this.$store.getters['lensOrder/getPrototype'](SIDE_RIGHT)?.code;
            const leftPrototypeCode  = this.$store.getters['lensOrder/getPrototype'](SIDE_LEFT) ?.code;

            return [rightPrototypeCode, leftPrototypeCode].filter(
                p => !!p // ignore empty
            ).some(
                p => !PROTOTYPES_WITHOUT_OVER_REFRACTION_CALCULATOR.includes(p)
            );
        },

        hasCalculators()
        {
            const rightPrototypeCode = this.$store.getters['lensOrder/getPrototype'](SIDE_RIGHT)?.code;
            const leftPrototypeCode  = this.$store.getters['lensOrder/getPrototype'](SIDE_LEFT) ?.code;

            return !PROTOTYPES_WITHOUT_CALCULATORS.some(
                p => [rightPrototypeCode, leftPrototypeCode].includes(p)
            );
        },

        /**
         * Used in handlers!
         */
        cEntity()
        {
            return this.$store.state.account.cEntity;
        },

        isOrderEdition()
        {
            return this.$route.name === 'edit-order-lenses';
        },

        viewAsCustomer()
        {
            return this.$store.getters['lensOrder/viewAsCustomer'];
        },

        dispatchErrorTitle()
        {
            if(this.dispatchError === null)
            {
                return null;
            }

            if(this.dispatchError instanceof UnknownPrototypeError)
            {
                return this.$t('order.errors.dispatch.unknown_prototype.title');
            }

            return this.dispatchError.message ||
                this.$t('order.errors.dispatch.unknown.title');
        },

        dispatchErrorText()
        {
            if(this.dispatchError === null)
            {
                return null;
            }

            if(this.dispatchError instanceof UnknownPrototypeError)
            {
                return this.$t('order.errors.dispatch.unknown_prototype.text', {
                    proto: this.dispatchError.prototypeCode,
                });
            }

            return this.dispatchError.message ||
                this.$t('order.errors.dispatch.unknown.text');
        },

        addToBasketButtonLabel()
        {
            const label = [this.$t('order.lenses.sections.add-to-basket-btn')];
            const hasRightFitting = !!this.$store.getters['lensOrder/getFitting'](SIDE_RIGHT);
            const hasLeftFitting  = !!this.$store.getters['lensOrder/getFitting'](SIDE_LEFT);

            if(
                this.$store.state.lensOrder.fittings.right.length <= 2 &&
                this.$store.state.lensOrder.fittings.left.length <= 2
            )
            {
                return label[0];
            }

            if(hasRightFitting || hasLeftFitting)
            {
                label.push(' (');
            }

            if(hasRightFitting)
            {
                label.push(this.getLensLetter(this.$store.state.lensOrder.fittings.current.right));
            }

            if(hasRightFitting && hasLeftFitting)
            {
                label.push('/');
            }

            if(hasLeftFitting)
            {
                label.push(this.getLensLetter(this.$store.state.lensOrder.fittings.current.left));
            }

            if(hasRightFitting || hasLeftFitting)
            {
                label.push(')');
            }

            return label.join('');
        },

        /**
         * The number of fittings currently being edited.
         */
        nbFittings()
        {
            let nb = 0;
            for(const side of [SIDE_RIGHT, SIDE_LEFT])
            {
                if(!!this.$store.getters['lensOrder/getFitting'](side))
                {
                    nb += 1;
                }
            }

            return nb;
        },
    },


    // ------------------------------------------------------------ WATCHERS

    watch:
    {
        /**
         * Unset right prototye code if a non-null order identifier is given.
         *
         * @param {*} orderIdentifier
         */
        'sides.right.orderIdentifier'(orderIdentifier)
        {
            if(orderIdentifier)
            {
                this.sides.right.prototypeCode = null;
            }

            this.sides.right.error = null;
            this.isEditButtonActive = true;
        },

        'sides.right.prototypeCode'()
        {
            this.isEditButtonActive = true;
        },

        /**
         * Unset left prototye code if a non-null order identifier is given.
         *
         * @param {*} orderIdentifier
         */
        'sides.left.orderIdentifier'(orderIdentifier)
        {
            if(orderIdentifier)
            {
                this.sides.left.prototypeCode = null;
            }

            this.sides.left.error = null;
            this.isEditButtonActive = true;
        },

        'sides.left.prototypeCode'()
        {
            this.isEditButtonActive = true;
        },

        '$route.params'(newParams, oldParams)
        {
            // Avoid retemplating when adding to basket
            if(!['order-lenses', 'edit-order-lenses'].includes(this.$route.name))
            {
                console.log('route has changed: dispatch avoided.');
                return;
            }

            this.lensOrderUrlManager.setFromRoute(this.$route);

            // Exception when user clicks on "New Order" menu
            if(!newParams.sides)
            {
                this.sides.right.orderIdentifier = null;
                this.sides.right.previousOrder   = null;
                this.sides.right.prototypeCode   = null;

                this.sides.left.orderIdentifier  = null;
                this.sides.left.previousOrder    = null;
                this.sides.left.prototypeCode    = null;

                this.editPrototypesSelection = false;

                // Reset both sides
                this.$store.commit('lensOrder/resetFitting');
            }
            else if(this.dispatchOnRouteChange)
            {
                console.log('reacting to route changes!', oldParams, newParams);

                this.syncDataFromUrl();
                this.dispatch();
            }
            else
            {
                console.log('not reacting to route changes!');
            }
        },
    },


    // ------------------------------------------------------------ LIFECYCLE

    created()
    {
        this.setupDispatchQueue();
        this.setupAddToBasketQueue();

        this.lensOrderUrlManager = new LensOrderUrlManager(this.$route);

        this.syncDataFromUrl();

        if(
            this.lensOrderUrlManager.has(SIDE_RIGHT) ||
            this.lensOrderUrlManager.has(SIDE_LEFT)
        )
        {
            this.initializing = true;
            this.dispatch();
        }
        else
        {
            console.log('Nothing on either side at creation');
        }
    },

    unmounted()
    {
        delete this.$route.params.sides; // IMPORTANT
        this.$store.commit('lensOrder/resetFitting'); // reset both sides
    },


    // ------------------------------------------------------------ METHODS

    methods:
    {
        // ---------------------------------------- Helper

        syncDataFromUrl()
        {
            for(const side of [SIDE_RIGHT, SIDE_LEFT])
            {
                const type = this.lensOrderUrlManager.getIdentifierType(side);
                this.sides[side].orderIdentifier = SUPPORTED_ID_TYPES.includes(type)
                    ? this.lensOrderUrlManager.getOrderIdentifier(side)
                    : null;
                this.sides[side].prototypeCode = this.lensOrderUrlManager.getPrototypeCode(side);

                if(!this.lensOrderUrlManager.has(side))
                {
                    this.sides[side].previousOrder = null;
                }
            }
        },


        // ---------------------------------------- Dispatch

        async dispatch(from = null)
        {
            console.log(`DISPATCH START! (from: ${from})`);

            let firstHandlerFound = false;

            // If no "from" has been given.
            if(!from)
            {
                // Set first key as "from".
                from = dispatchQueue.keys().next().value;
            }

            this.loading = true;
            this.editPrototypesSelection = false;

            for(const [key, handler] of dispatchQueue.entries())
            {
                if(key.includes(from))
                {
                    firstHandlerFound = true;
                }

                if(firstHandlerFound)
                {
                    console.groupCollapsed('Handler: ' + key);

                    try
                    {
                        const result = await handler.handle();
                        if(result === false)
                        {
                            console.groupEnd();
                            console.log('DISPATCH BREAK!');
                            break;
                        }
                    }
                    catch(error)
                    {
                        console.groupEnd();
                        console.log('DISPATCH BREAK!', error);
                        this.dispatchError = error;
                        break;
                    }

                    console.groupEnd();
                }
            }

            // Clear temporary data that was set elsewhere before navigating
            this.$store.dispatch('lensOrder/setTemplateData', null);

            this.loading = false;
            this.initializing = false;

            console.log('DISPATCH END!');
        },


        // ---------------------------------------- Add to Basket

        async addToBasket(from = null)
        {
            console.log(`ADD TO BASKET START! (from ${from})`);

            this.$store.commit('overlay/setVisibility', true);

            // If no "from" has been given => start from the beginning.
            if(!from)
            {
                from = addToBasketQueue.keys().next().value;
            }

            let firstHandlerFound = false;
            for(const [key, handler] of addToBasketQueue.entries())
            {
                if(key.includes(from))
                {
                    firstHandlerFound = true;
                }

                if(!firstHandlerFound)
                {
                    continue;
                }

                console.groupCollapsed('Handler: ' + key);

                try
                {
                    const result = await handler.handle();

                    if(result === false)
                    {
                        console.groupEnd();
                        console.log('ADD TO BASKET BREAK!');
                        break;
                    }
                }
                catch(error)
                {
                    console.groupEnd();
                    console.log('ADD TO BASKET BREAK!', error);
                    break;
                }

                console.groupEnd();
            }

            this.$store.commit('overlay/setVisibility', false);

            console.log('ADD TO BASKET END!');
        },


        // ---------------------------------------- Update

        /**
         * Update the order(s) currently being edited.
         *
         * @param {?Record<string, any>} [options] Optional key/value pairs of
         *                                         options to be passed to the
         *                                         `UpdateOrders`handler.
         * @returns {void}
         */
        async updateOrders(options = null)
        {
            const queue = new Map();
            queue.set('ValidateLenses', new ValidateLenses(this));
            queue.set('PriceLenses',    new PriceLenses(this));
            queue.set('UpdateOrders',   new UpdateOrders(this, options));
            if(options?.release_from_review)
            {
                // After releasing an order from review, we should redirect the
                // user to the home page, and refresh the list of orders waiting
                // for review.
                queue.set('NavigateHome', new this.Navigate('home'));
                if(this.$store.getters['account/isStaff'])
                {
                    queue.set(
                        'RefreshWaitingForReviewOrdersList',
                        new RefreshWaitingForReviewOrdersList(this)
                    );
                }
            }
            else
            {
                // Otherwise, redirect them to "My Reviews".
                queue.set(
                    'NavigateToMyReviews',
                    new this.Navigate('entity-review-requests')
                );
            }


            await this.process('update orders', queue);
        },

        /**
         * Handler for the "Production" button.
         */
        async productionBtn_onClick()
        {
            if(!this.$store.getters['account/isStaff'])
            {
                // Non-staff members must confirm before sending to production
                this.confirmProductionDialogOpen = true;
                return;
            }

            // Staff members don't need to confirm
            await this.releaseOrdersFromReview();
        },

        /**
         * Update the orders and release them from review.
         */
        async releaseOrdersFromReview()
        {
            try
            {
                await this.updateOrders({ release_from_review: true });
            }
            catch(err)
            {
                // todo: notify of error
                console.error('could not send orders to production', err);
            }
        },

        /**
         * Process a chain of responsibility.
         *
         * @param {string} name The name of the queue.
         * @param {Map<string, {handle: () => Promise<boolean>}>} queue The
         * chain of handlers.
         * @async
         * @returns {Promise<void>}
         */
        async process(name, queue)
        {
            console.log('Processing queue', name);

            this.$store.commit('overlay/setVisibility', true);

            for(const [key, handler] of queue.entries())
            {
                console.groupCollapsed('Handler: ' + key);

                try
                {
                    const result = await handler.handle();
                    if(result === false)
                    {
                        throw new Error(`Handler ${key} failed`);
                    }
                }
                catch(error)
                {
                    console.groupEnd();
                    console.log('Stopping queue', { name, error });
                    break;
                }

                console.groupEnd();
            }

            this.$store.commit('overlay/setVisibility', false);

            console.log('Done processing queue', name);
        },


        // ---------------------------------------- Next

        next()
        {
            if(!this.isNextButtonActive || !this.isEditButtonActive)
            {
                return;
            }

            for(const side of [SIDE_RIGHT, SIDE_LEFT])
            {
                const orderIdentifier = this.sides[side].orderIdentifier;

                // Detect if the serial number is from SwissLens or an OEM
                if(ONLY_NUMBERS.test(orderIdentifier))
                {
                    if(orderIdentifier < 30_000_000)
                    {
                        // Only numbers < 30'000'000 => SwissLens Serial Number
                        this.lensOrderUrlManager.setLensSn(side, orderIdentifier);
                    }
                    else
                    {
                        // Only numbers > 30'000'000 => SwissLens Order reference
                        this.lensOrderUrlManager.setOrderReference(side, orderIdentifier);
                    }
                }
                else
                {
                    // Not only numbers => OEM Number
                    this.lensOrderUrlManager.setOemNumber(side, orderIdentifier);
                }

                let targetProtoCode = this.sides[side].prototypeCode;

                // Find matching variant for the current model
                const cProtoCode = this.lensOrderUrlManager.getPrototypeCode(side);
                if(cProtoCode)
                {
                    const {
                        variant_code: cVariant,
                    } = this.$store.getters['prototypes/getByCode'](cProtoCode) ?? {};
                    const {
                        model_code:   selModel,
                        variant_code: selVariant,
                    } = this.$store.getters['prototypes/getByCode'](targetProtoCode) ?? {};
                    if(cVariant !== selVariant)
                    {
                        // The current and selected variants don't match.
                        // Find one that does for the selected model.
                        const protos = this.$store.getters['prototypes/allByModelCode'](selModel);
                        for(let i = 0; i < protos.length; i++)
                        {
                            const {
                                code,
                                variant_code: variant,
                            } = protos[i];
                            if(variant === cVariant)
                            {
                                console.log(`Found variant match for ${cProtoCode} -> ${targetProtoCode} ` +
                                    `with model ${selModel} and variant ${cVariant}: ${code}`);
                                targetProtoCode = code;
                                break;
                            }
                        }
                    }
                }

                this.lensOrderUrlManager.setPrototypeCode(side, targetProtoCode);
            }

            this.$router.push({ path: this.lensOrderUrlManager.getPath() });
        },


        // ---------------------------------------- Invert

        invertAndDispatch(from)
        {
            [this.sides.left, this.sides.right] = [this.sides.right, this.sides.left];
            this.lensOrderUrlManager.invert();

            this.dispatch(from);
        },


        // ------------------------------------------------------------ PROTOTYPE SET FROM LIST

        onPrototypeSet(side, prototypeCode)
        {
            this.sides[side].prototypeCode = prototypeCode;
        },


        // ------------------------------------------------------------ FITTING

        toggleFittingsPanel()
        {
            this.fittingsPanelOpen = !this.fittingsPanelOpen;

            if(this.fittingsPanelOpen)
            {
                this.closeCalculatorPanels();
            }
        },

        closeFittingsPanel()
        {
            this.fittingsPanelOpen = false;
        },


        // ------------------------------------------------------------ CALCULATORS

        toggleCalculatorPanels()
        {
            this.calculatorPanelsOpen = !this.calculatorPanelsOpen;
        },

        closeCalculatorPanels()
        {
            this.calculatorPanelsOpen = false;
        },


        // ------------------------------------------------------------ VIEW AS CUSTOMER

        toggleViewAsCustomer()
        {
            this.$store.dispatch('lensOrder/toggleViewAsCustomer');
            this.$store.dispatch('lensOrder/templateAndValidate', {
                options:
                {
                    only_present: true,
                    no_required:  true,
                },
            });
        },


        // ------------------------------------------------------------ SET UP CHAINS OF RESPONSIBILITY

        setupDispatchQueue()
        {
            dispatchQueue.set('ResetErrors',                                     new ResetErrors(this));
            dispatchQueue.set('ResetSideFittingOnConditions-right',              new ResetSideFittingOnConditions(this, SIDE_RIGHT));
            dispatchQueue.set('ResetSideFittingOnConditions-left',               new ResetSideFittingOnConditions(this, SIDE_LEFT));
            dispatchQueue.set('FetchPreviousOrderByReference-right',             new FetchPreviousOrderByReference(this, SIDE_RIGHT));     // if previous order has changed
            dispatchQueue.set('FetchPreviousOrderByReference-left',              new FetchPreviousOrderByReference(this, SIDE_LEFT));      // if previous order has changed
            dispatchQueue.set('FetchPreviousOrderByLensSn-right',                new FetchPreviousOrderByLensSn(this, SIDE_RIGHT));        // if previous order has changed
            dispatchQueue.set('FetchPreviousOrderByLensSn-left',                 new FetchPreviousOrderByLensSn(this, SIDE_LEFT));         // if previous order has changed
            dispatchQueue.set('FetchPreviousOrderByLensOemNumber-right',         new FetchPreviousOrderByLensOemNumber(this, SIDE_RIGHT)); // if previous order has changed
            dispatchQueue.set('FetchPreviousOrderByLensOemNumber-left',          new FetchPreviousOrderByLensOemNumber(this, SIDE_LEFT));  // if previous order has changed
            dispatchQueue.set('CheckForSomethingOrStop',                         new CheckForSomethingOrStop(this));                       // if stop: loading = false
            dispatchQueue.set('ValidatePreviousOrdersEntitiesConsistency',       new ValidatePreviousOrdersEntitiesConsistency(this));     // only re-order
            dispatchQueue.set('ValidatePreviousOrdersPatientsConsistency',       new ValidatePreviousOrdersPatientsConsistency(this));     // only re-order
            dispatchQueue.set('ValidateLensSidesConsistency',                    new ValidateLensSidesConsistency(this));                  // only re-order
            dispatchQueue.set('SilentlySetPrototypeCodesInUrl',                  new SilentlySetPrototypeCodesInUrl(this));
            dispatchQueue.set('CreateFittingIfNotExists-right',                  new CreateFittingIfNotExists(this, SIDE_RIGHT));
            dispatchQueue.set('CreateFittingIfNotExists-left',                   new CreateFittingIfNotExists(this, SIDE_LEFT));
            dispatchQueue.set('SetFittingPrototype-right',                       new SetFittingPrototype(this, SIDE_RIGHT));
            dispatchQueue.set('SetFittingPrototype-left',                        new SetFittingPrototype(this, SIDE_LEFT));
            dispatchQueue.set('SetFittingValuesByPreviousOrder-right',           new SetFittingValuesByPreviousOrder(this, SIDE_RIGHT));
            dispatchQueue.set('SetFittingValuesByPreviousOrder-left',            new SetFittingValuesByPreviousOrder(this, SIDE_LEFT));
            dispatchQueue.set('SetFittingValuesByBasketLine-right',              new SetFittingValuesByBasketLine(this, SIDE_RIGHT));
            dispatchQueue.set('SetFittingValuesByBasketLine-left',               new SetFittingValuesByBasketLine(this, SIDE_LEFT));
            dispatchQueue.set('SetFittingSessionId-right',                       new SetFittingSessionId(this, SIDE_RIGHT));
            dispatchQueue.set('SetFittingSessionId-left',                        new SetFittingSessionId(this, SIDE_LEFT));
            dispatchQueue.set('SetPrototypePreviousValuesByPreviousOrder-right', new SetPrototypePreviousValuesByPreviousOrder(this, SIDE_RIGHT));
            dispatchQueue.set('SetPrototypePreviousValuesByPreviousOrder-left',  new SetPrototypePreviousValuesByPreviousOrder(this, SIDE_LEFT));
            dispatchQueue.set('SetInitialLensSide-right',                        new this.SetInitialLensSide(SIDE_RIGHT));
            dispatchQueue.set('SetInitialLensSide-left',                         new this.SetInitialLensSide(SIDE_LEFT));
            dispatchQueue.set('FetchReviewRequest-right',                        new FetchReviewRequest(this, SIDE_RIGHT));
            dispatchQueue.set('FetchReviewRequest-left',                         new FetchReviewRequest(this, SIDE_LEFT));
            dispatchQueue.set('TemplatePrototypeForms-right',                    new TemplatePrototypeForms(this, SIDE_RIGHT));
            dispatchQueue.set('TemplatePrototypeForms-left',                     new TemplatePrototypeForms(this, SIDE_LEFT));
            dispatchQueue.set('SetPrototypesSide',                               new SetPrototypesSide(this));
            dispatchQueue.set('CompleteGroupedParameterValues',                  new CompleteGroupedParameterValues(this));
        },

        setupAddToBasketQueue()
        {
            addToBasketQueue.set('ValidateLenses',               new ValidateLenses(this));
            addToBasketQueue.set('PriceLenses',                  new PriceLenses(this));
            addToBasketQueue.set('SetLensPairId',                new this.SetLensPairId());
            addToBasketQueue.set('InitializeAddToBasketContext', new this.InitializeAddToBasketContext());
            addToBasketQueue.set('ConvertFittingsToBasketLines', new this.ConvertFittingsToBasketLines());
            addToBasketQueue.set('LinkLinesToBeSaved',           new this.LinkLinesToBeSaved());
            addToBasketQueue.set('AddToBasket',                  new this.AddToBasket());
            addToBasketQueue.set('NavigateToBasket',             new NavigateToBasket(this));
        },
    },
};
</script>


<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style>
@reference "#main.css";

.sl-eyes-header--fixed
{
    padding-top: 40px; /* WARNING: Must be the same as eyes-header height! */
}

.sl-order__submit
{
    @apply outline-none;

    &:hover,
    &:focus
    {
        @apply bg-primary-400;
    }

    &:focus-visible
    {
        @apply ring-2 ring-primary-400 ring-offset-2;
    }

    &:active
    {
        @apply bg-primary-600;
    }
}
</style>
